import type * as clientApiViewModel from '~/clientApi';
import { Creator } from '~/clientApi/creator.model';
import { PRIMARY_SOCIAL_PLATFORMS, SOCIAL_PLATFORMS } from '~/constants';
import {
  CreatorLocationDetail,
  ILocation,
} from '~/screens/MyAgency/components/SearchLocation';
import { ISearchContentCreator } from '~/searchApi';
import { ICreator } from '~/types';

import {
  ICreatorMainPlatformData,
  ICreatorPlatformStatus,
} from './creator.types';

/**
 * Get general data for creator biased toward platform heirarchy
 */
export const getMainPlatformDataV2 = (creator: Creator) => {
  const { fullName, avatar } = creator;
  const { instagram, tiktok, snapchat, youtube } = creator.platforms ?? {};

  // Format snapchat location if available
  const snapchatLocation =
    snapchat && (snapchat.city || snapchat.country)
      ? {
          city: snapchat.city,
          country: snapchat.country,
          formatted: [snapchat.city, snapchat.country]
            .filter(Boolean)
            .join(', '),
        }
      : undefined;
  const handle =
    instagram?.handle ||
    tiktok?.handle ||
    snapchat?.handle ||
    youtube?.handle ||
    undefined;

  return {
    avatar,
    email: creator?.email || undefined,
    handle,
    identifierForUi: handle ? `@${handle}` : creator?.email,
    name: fullName,
    location:
      instagram?.location ||
      tiktok?.location ||
      snapchatLocation ||
      youtube?.location ||
      undefined,
  };
};

export const getMainPlatformData = (
  creator?: Partial<ICreator | ISearchContentCreator>,
): ICreatorMainPlatformData => {
  if (creator) {
    const { fullName, avatar } = creator;
    const { instagram, tiktok, snapchat, youtube } = creator.platforms ?? {};

    return {
      avatar,
      email: creator?.email || undefined,
      handle:
        instagram?.handle ||
        tiktok?.handle ||
        snapchat?.handle ||
        youtube?.handle ||
        undefined,
      name: fullName,
      location:
        instagram?.location ||
        tiktok?.location ||
        snapchat?.location ||
        youtube?.location ||
        undefined,
    };
  }

  return {};
};

export const getPlatformData = (
  creator: ICreator,
  platform: SOCIAL_PLATFORMS,
): clientApiViewModel.ICreatorPlatformStatus =>
  creator.platforms[platform] || {
    isConnected: false,
    isTokenValid: false,
    reminderSentCount: 0,
    shouldReAuth: false,
  };

export const getPlatformStatus = (
  { platforms }: ICreator,
  platformsStatus: SOCIAL_PLATFORMS[] = [
    SOCIAL_PLATFORMS.instagram,
    SOCIAL_PLATFORMS.tiktok,
    SOCIAL_PLATFORMS.youtube,
  ],
): ICreatorPlatformStatus => {
  const { connected, expired, shouldReAuth } = platformsStatus.reduce(
    (
      acc: Pick<
        ICreatorPlatformStatus,
        'connected' | 'expired' | 'shouldReAuth'
      >,
      platform,
    ) => {
      const {
        isConnected = false,
        isTokenValid = false,
        shouldReAuth = false,
      } = platforms[platform] || {};
      if (isConnected) {
        acc.connected.push(platform);
        if (!isTokenValid) {
          acc.expired.push(platform);
        }
        if (shouldReAuth) {
          acc.shouldReAuth.push(platform);
        }
      }

      return acc;
    },
    {
      connected: [],
      expired: [],
      shouldReAuth: [],
    },
  );

  return {
    connected,
    expired,
    hasConnected: !!connected.length,
    hasExpired: !!expired.length,
    hasReAuth: !!shouldReAuth.length,
    shouldReAuth,
  };
};

export const getMainPlatformStatus = (
  creator: ICreator,
): ICreatorPlatformStatus =>
  getPlatformStatus(creator, Array.from(PRIMARY_SOCIAL_PLATFORMS));

export const getCreatorLocation = (creator: ICreator) => {
  const location = creator?.location as CreatorLocationDetail & ILocation;

  if (!location) return creator?.city || '';

  const { city, regionName, regionCode, country, countryCodeAlpha3 } = location;
  return [
    city,
    city ? regionCode : regionName,
    city || regionName ? countryCodeAlpha3 : country,
  ]
    .filter(Boolean)
    .join(', ');
};
